import { faArrowRight, faBed, faPerson, faPersonWalking, faBedPulse, faCheckCircle, faMoon, faSun, faExclamationCircle, faHospital, faUserDoctor, faChevronDown, faChevronUp, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'chartjs-adapter-moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, ButtonGroup, Card, Col, Container, OverlayTrigger, Placeholder, Popover, Row, Tab, Tabs, Tooltip } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import { useParams } from 'react-router-dom';
import { callFhirEncounters } from '../../amplify/appsync/fetch-sync-fhir';
import { COMPASS_ANNOTATIONS_BY_STUDY_ID, COMPASS_NUMERICS, COMPASS_PARTICIPANT_DETAILS, COMPASS_RAW_DATA, COMPASS_SURVEYS_BY_PARTICIPANT_ID, PATIENT_VISIT_BY_CSN, callQuery } from '../../amplify/appsync/query';
import { generateDates, getDaysBetweenDates, getDayTruncatedDate, getLocalDateStrFromDate, getLocalTimeStrFromDate, getPacificDateString, subtractHours, withTimezone } from '../../utils/time';
import './Compass.css';
import { NumericalVitalData, NumericsDataPoint, WaveformVitalData } from './VitalDataTypes';
import VitalSigns from './VitalSignDisplay';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { ProcessedPatientData } from './DataExport';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import DataExport from './DataExport';
import { CompassTrends } from './CompassTrends';
import { getEQ5DScore } from './VitalSignStyles';
import { callCalculateVitalsAbnormalities } from '../../amplify/appsync/calculate-vitals-abnormalities';

export const SURVEY_RESPONSE: any = {
    "eq_1": {
        "1": "I have no problems walking",
        "2": "I have slight problems",
        "3": "I have moderate problems",
        "4": "I have severe problems",
        "5": "I am unable to walk",
    },
    "eq_2": {
        "1": "I have no problems washing or dressing myself",
        "2": "I have slight problems",
        "3": "I have moderate problems",
        "4": "I have severe problems",
        "5": "I am unable to wash or dress myself",
    },
    "eq_3": {
        "1": "I have no problems",
        "2": "I have slight problems",
        "3": "I have moderate problems",
        "4": "I have severe problems",
        "5": "I am unable to perform these activities",
    },
    "eq_4": {
        "1": "I have no pain or discomfort",
        "2": "I have slight pain or discomfort",
        "3": "I have moderate pain or discomfort",
        "4": "I have severe pain or discomfort",
        "5": "I have extreme pain or discomfort",
    },
    "eq_5": {
        "1": "I am not anxious or depressed",
        "2": "I am slightly anxious or depressed",
        "3": "I am moderately anxious or depressed",
        "4": "I am severely anxious or depressed",
        "5": "I am extremely anxious or depressed",
    },
};

const loadNumerics = async (studyId: string | undefined, serialNumber: string, localDateStr: string): Promise<NumericalVitalData> => {
    if (!studyId) {
        return {
            heartRate: [],
            respiratoryRate: [],
            temperature: [],
            steps: [],
            eda: [],
            prv: [],
            activity: []
        };
    }

    try {
        const info = await callQuery({
            operation: COMPASS_NUMERICS,
            study_id: studyId,
            serial_number: serialNumber,
            local_date_str: localDateStr,
        });

        if (!info || !info.timestamp) {
            return {
                heartRate: [],
                respiratoryRate: [],
                temperature: [],
                steps: [],
                eda: [],
                prv: [],
                activity: []
            };
        }

        const createDataPoints = <T extends string | number>(dataKey: string): NumericsDataPoint<T>[] => {
            return info.timestamp.map((timestamp: number, index: number) => ({
                timestamp,
                value: info[dataKey] ? info[dataKey][index] : null
            }));
        };

        return {
            heartRate: createDataPoints<number>('pulse-rate'),
            respiratoryRate: createDataPoints<number>('respiratory-rate'),
            temperature: createDataPoints<number>('temperature'),
            steps: createDataPoints<number>('step-counts'),
            eda: createDataPoints<number>('eda'),
            prv: createDataPoints<number>('prv'),
            activity: createDataPoints<string>('activity-classification')
        };

    } catch (error) {
        console.error(`Error loading numerics:`, error);
        return {
            heartRate: [],
            respiratoryRate: [],
            temperature: [],
            steps: [],
            eda: [],
            prv: [],
            activity: []
        };
    }
};

export const CompassDashboard = (props: { user: any }) => {
    let { studyID } = useParams();

    const [isLoadingEncounters, setIsLoadingEncounters] = useState<boolean>(false);
    const [indexPatientVisit, setIndexPatientVisit] = useState<PatientVisit | undefined>();
    const [compassParticipant, setCompassParticipant] = useState<any>();
    const [encounters, setEncounters] = useState<any>([]);
    const [compassActiveDate, setCompassActiveDate] = useState<string>();
    const [compassDates, setCompassDates] = useState<Date[]>([]);
    const [dateToSurveyAvailability, setDateToSurveyAvailability] = useState<any>({});
    const [dateToMonitorAvailability, setDateToMonitorAvailability] = useState<any>({});
    const [dateToHospital, setDateToHospital] = useState<any>({});
    const [dateToED, setDateToED] = useState<any>({});
    const [dateToNonHospital, setDateToNonHospital] = useState<any>({});
    const [dailyKeyToData, setDailyKeyToData] = useState<any>({});
    const [showVitalsTrends, setShowVitalsTrends] = useState<boolean>(false);
    const [showVitalsDay, setShowVitalsDay] = useState<boolean>(true);
    const [showVitalsNight, setShowVitalsNight] = useState<boolean>(false);
    const [showVitalsWake, setShowVitalsWake] = useState<boolean>(false);
    const [showVitalsSleep, setShowVitalsSleep] = useState<boolean>(false);
    const [showVitalsActive, setShowVitalsActive] = useState<boolean>(true);
    const [showVitalsResting, setShowVitalsResting] = useState<boolean>(true);
    const [showVitalsMin, setShowVitalsMin] = useState<boolean>(false);
    const [showVitalsAvg, setShowVitalsAvg] = useState<boolean>(true);
    const [showVitalsMax, setShowVitalsMax] = useState<boolean>(false);
    const [show30Days, setShow30Days] = useState<boolean>(true);

    const [isLoadingMetricToTrends, setIsLoadingMetricToTrends] = useState<boolean>(false);
    const [metricToTrends, setMetricToTrends] = useState<any>({});
    const [eq5DCorr, setEq5DCorr] = useState<any>([]);
    const [eq6Corr, setEq6Corr] = useState<any>([]);

    const [currentTab, setCurrentTab] = useState<string>('trends');

    const [numerics, setNumerics] = useState<NumericalVitalData>({
        heartRate: [],
        respiratoryRate: [],
        temperature: [],
        steps: [],
        eda: [],
        prv: [],
        activity: []
    });
    const [rawData, setRawData] = useState<WaveformVitalData>(
        {
            bvp: [],
            eda: [],
            accelerometer_x: [],
            accelerometer_y: [],
            accelerometer_z: [],
            gyroscope: [],
            temperature: [],
            steps: [],
            timestamp: 0
        }
    );
    const [isLoadingNumerics, setIsLoadingNumerics] = useState(false);
    const [isLoadingWaveform, setIsLoadingWaveform] = useState(false);
    const [patient, setPatient] = useState<ProcessedPatientData>({
        dateToSurveyAvailability: {},
        dailyKeyToData: {},
        dateToMonitorAvailability: {},
        dateToHospital: {},
        dateToED: {},
        dateToNonHospital: {},
        compassDates: [],
        studyId: studyID,
        patientName: undefined
    });

    let serialNumber = useRef<string>();

    useEffect(() => {
        if (studyID) {
            Promise.all([
                callQuery({
                    operation: COMPASS_PARTICIPANT_DETAILS,
                    study_id: studyID,
                }),
                callQuery({
                    operation: COMPASS_ANNOTATIONS_BY_STUDY_ID,
                    study_id: studyID,
                    granularity: "daily",
                }),
            ])
            .then((result: any) => {
                let infoParticipant = result[0];
                if (infoParticipant.length > 0) {
                    infoParticipant = infoParticipant[0];
                    let endDate = withTimezone(infoParticipant.end_date ? new Date(infoParticipant.end_date) : new Date(), "America/Los_Angeles");
                    const tPlus31Date = withTimezone(subtractHours(new Date(infoParticipant.enrollment_date), -24 * 30), "America/Los_Angeles");
                    endDate = endDate > tPlus31Date ? endDate : tPlus31Date;
                    const dateRange = generateDates(getDayTruncatedDate(withTimezone(new Date(infoParticipant.enrollment_date), "America/Los_Angeles")), getDayTruncatedDate(endDate));
                    setCompassDates(dateRange);
                    console.log("setting serial", infoParticipant)
                    serialNumber.current = infoParticipant.serial_number;
                    setCompassParticipant(infoParticipant);

                    setIsLoadingEncounters(true);
                    callFhirEncounters({
                        fhir_id: infoParticipant.fhir_id,
                        from_date: dateRange[0].toISOString().split('.')[0]+"Z",
                        to_date: dateRange[dateRange.length - 1].toISOString().split('.')[0]+"Z",
                    }).then((encounters) => {
                        setIsLoadingEncounters(false);
                        setEncounters(encounters);
                    });

                    callQuery({
                        operation: PATIENT_VISIT_BY_CSN,
                        csn: infoParticipant.csn,
                    })
                    .then((patients: PatientVisit[]) => {
                        for (var patient of patients) {
                            setIndexPatientVisit(patient);
                        }
                    });

                    setIsLoadingMetricToTrends(true);
                    callCalculateVitalsAbnormalities(JSON.stringify({
                        compass_participant: studyID,
                        mrn: infoParticipant.mrn,
                        index_csn: infoParticipant.csn,
                    })).then((output: any) => {
                        try {
                            setIsLoadingMetricToTrends(false);
                            const response = JSON.parse(JSON.parse(output).body.replace(/\bNaN\b/g, "null")).response;
                            setMetricToTrends(response["metric_to_trend"])
                            setEq5DCorr(response["eq5d_df"]);
                            setEq6Corr(response["eq6_df"]);
                        } catch {
                            console.error("Could not parse response", output);
                        }
                    });
                }


                const infoAnnotations = result[1];
                // console.log("infoAnnotations", infoAnnotations);
                const timeToMonitor: any = {};
                const timeToHospitalVisit: any = {};
                const timeToEDVisit: any = {};
                const timeToNonHospitalVisit: any = {};
                const dailyKeyToData: any = {};
                let localDateStr = "";
                for (let row of infoAnnotations) {
                    // row[0] will look like 2024-12-14T00:00:00.000Z
                    // but it has already been converted to local time as part of the 
                    // underlying SQL query. Therefore, we just take the first 10 characters as-is.
                    let dt = row[0].substring(0, 10);
                    if (Object.keys(timeToMonitor).length === 0) {
                        setCompassActiveDate(dt);
                        localDateStr = dt;
                    }
                    if (row[1] === "WEARING_DETECTION") {
                        timeToMonitor[dt] = Number(row[2]);
                    } else if (row[1] === "VISIT_HOSPITAL") {
                        timeToHospitalVisit[dt] = Number(row[2]);
                    } else if (row[1] === "VISIT_ED") {
                        timeToEDVisit[dt] = Number(row[2]);
                    } else if (row[1] === "VISIT_NON_HOSPITAL") {
                        timeToNonHospitalVisit[dt] = Number(row[2]);
                    } else {
                        if (!(row[1] in dailyKeyToData)) {
                            dailyKeyToData[row[1]] = {}
                        }
                        dailyKeyToData[row[1]][dt] = row[2];
                    }
                }
                setDateToMonitorAvailability(timeToMonitor);
                setDateToHospital(timeToHospitalVisit);
                setDateToED(timeToEDVisit);
                setDateToNonHospital(timeToNonHospitalVisit);
                setDailyKeyToData(dailyKeyToData);
            });

            callQuery({
                operation: COMPASS_SURVEYS_BY_PARTICIPANT_ID,
                study_id: studyID,
            })
            .then((info: any) => {
                const timeToSurvey: any = {};
                for (let row of info) {
                    let dt = getPacificDateString(new Date(row.start_date));
                    timeToSurvey[dt] = row;
                }
                setDateToSurveyAvailability(timeToSurvey);
            });
        }
    }, [studyID]);

    useEffect(() => {
        if (compassDates.length > 0) {
            setPatient({
                dateToSurveyAvailability,
                dailyKeyToData,
                dateToMonitorAvailability,
                dateToHospital,
                dateToED,
                dateToNonHospital,
                compassDates,
                studyId: studyID,
                patientName: compassParticipant?.mrn
            });
        }
    }, [
        dateToSurveyAvailability,
        dailyKeyToData,
        dateToMonitorAvailability,
        dateToHospital,
        dateToED,
        dateToNonHospital,
        compassDates,
        studyID,
        compassParticipant
    ]);

    const getRawData = async (serialNumber: string, localDateStr: string, epochTimeSec: number) => {
        setIsLoadingWaveform(true);
        try {
            const info = await callQuery({
                operation: COMPASS_RAW_DATA,
                study_id: studyID,
                serial_number: serialNumber,
                local_date_str: localDateStr,
                target_epoch_sec: epochTimeSec,
            });
            // Add timestamp to the raw data
            setRawData({
                ...info,
                timestamp: epochTimeSec
            });
            console.log(info);
        } catch (error) {
            console.error("Error fetching raw data:", error);
            setRawData(
                {
                bvp: [],
                eda: [],
                accelerometer_x: [],
                accelerometer_y: [],
                accelerometer_z: [],
                gyroscope: [],
                temperature: [],
                steps: [],
                timestamp: 0
            });
        } finally {
            setIsLoadingWaveform(false);
        }
    };

    const filteredEncounters = useMemo(() => {
        if (encounters) {
            const filtered = encounters.filter((encounter: any) => {
                if (encounter.resource && encounter.resource.class && encounter.resource.class.display) {
                    if (encounter.resource.class.display !== "Support OP Encounter") {
                        return true;
                    }
                }
                return false;
            });
            return [].concat(filtered).sort((a: any, b: any) => a.resource.period.start > b.resource.period.start ? 1 : -1);
        } else {
            return [];
        }
    }, [encounters]);

    const getEncounterLabel = (encounter: any) => {
        if (encounter.resource && encounter.resource.period && encounter.resource.period.start) {
            if (compassActiveDate === getPacificDateString(new Date(encounter.resource.period.start))) {
                return (
                    <FontAwesomeIcon icon={faBedPulse} />
                );
            }
        }
        return <></>;
    };

    const redirectToDashboard = (csn: string) => {
        callQuery({
            operation: PATIENT_VISIT_BY_CSN,
            csn: csn,
        })
        .then((info: PatientVisit[]) => {
            if (info && info.length > 0 && info[0].internal_csn) {
                window.open(`/dashboard/${info[0].internal_csn}`, "_blank");
            }
        });
    };

    useEffect(() => {
        if (compassActiveDate && serialNumber.current) {
            setIsLoadingNumerics(true);
            loadNumerics(studyID, serialNumber.current, compassActiveDate)
                .then(data => {
                    setIsLoadingNumerics(false);
                    setNumerics(data);
                });
            getRawData(serialNumber.current, compassActiveDate, Math.round(new Date(compassActiveDate + " 10:00:00").getTime() / 1000));
        }
    }, [compassActiveDate]);

    const formatSurveyResponse = (val: string) => {
        if (val === "NaN") {
            return "-"
        } else {
            return val;
        }
    };

    const vitalTrendKeys = useMemo(() => {
        const keys = Object.keys(dailyKeyToData);
        keys.sort();
        return keys.filter(k => k !== "WAKE_TIME" && k !== "FIRST_SLEEP_TIME");
    }, [dailyKeyToData]);

    const formatVitalTrendLabel = (label: string) => {
        const isDay = label.includes("DAYTIME");
        const isNight = label.includes("NIGHTTIME");
        const isAvg = label.includes("_AVG");
        const isMin = label.includes("_MIN");
        const isMax = label.includes("_MAX");
        const isActive = label.includes("_ACTIVE");
        const isResting = label.includes("_RESTING");
        let root = label.split("_RESTING")[0].split("_ACTIVE")[0];
        root = root.replace("_", " ");
        return (
            <>
                {root} {isAvg ? "(Avg)" : isMin ? "(Min)" : isMax ? "(Max)" : ""}
                {
                    isDay ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Popover>
                                    <Popover.Body>
                                        Daytime
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <span style={{marginLeft: '6px'}}><FontAwesomeIcon icon={faSun} /></span>
                        </OverlayTrigger>
                    ) : isNight ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Popover>
                                    <Popover.Body>
                                        Nighttime
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <span style={{marginLeft: '6px'}}><FontAwesomeIcon icon={faMoon} /></span>
                        </OverlayTrigger>
                    ) : (<></>)
                }
                {
                    isActive ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Popover>
                                    <Popover.Body>
                                        Active
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <span style={{marginLeft: '6px'}}><FontAwesomeIcon icon={faPersonWalking} /></span>
                        </OverlayTrigger>
                    ) : isResting ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Popover>
                                    <Popover.Body>
                                        Resting
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <span style={{marginLeft: '6px'}}><FontAwesomeIcon icon={faPerson} /></span>
                        </OverlayTrigger>
                    ) : (<></>)
                }
            </>
        )
    }

    function getHeatmapColor(value: any, min: number, max: number) {
        if (value === null || value === undefined || max === min) {
            return 'inherit';
        }

        // Normalize the value to a range between 0 and 1
        const percentage = (value - min) / (max - min);
      
        // Define the colors for the Seaborn palette (light blue to dark blue)
        const colors = [
          [240, 249, 255], // Lightest blue
          [189, 215, 231], // Light blue
          [107, 174, 214], // Medium blue
          [33, 113, 181],  // Dark blue
          [8, 48, 107]     // Navy blue
        ];
      
        // Calculate the index of the two colors to interpolate between
        const index = Math.min(Math.floor(percentage * (colors.length - 1)), colors.length - 2);
        const t = (percentage * (colors.length - 1)) - index;
      
        // Interpolate between the two colors
        const color1 = colors[index];
        const color2 = colors[index + 1];
        const r = Math.round(color1[0] + t * (color2[0] - color1[0]));
        const g = Math.round(color1[1] + t * (color2[1] - color1[1]));
        const b = Math.round(color1[2] + t * (color2[2] - color1[2]));
      
        return `rgb(${r},${g},${b})`;
    }

    function getHeatmapFontColor(value: any, min: number, max: number) {
        if (value === null || value === undefined) {
            return 'inherit';
        }

        // Normalize the value to a range between 0 and 1
        const percentage = (value - min) / (max - min);

        if (percentage > 0.5) {
            return 'white';
        } else {
            return 'black';
        }
    }

    const setShowVitalsTimePeriod = (period: string) => {
        let day = false;
        let night = false;
        let wake = false;
        let sleep = false;
        if (period === "day") {
            day = true;
        } else if (period === "night") {
            night = true;
        } else if (period === "wake") {
            wake = true;
        } else if (period === "sleep") {
            sleep = true;
        }
        setShowVitalsDay(day);
        setShowVitalsNight(night);
        setShowVitalsWake(wake);
        setShowVitalsSleep(sleep);
    }

    const isReturnVisit = () => {
        if (compassDates) {
            let idx = 0;
            for (let compassDate of compassDates) {
                if (idx > 0 && dateToED[getPacificDateString(compassDate)]) {
                    return true;
                }
                idx++;
            }
        }
        return false;
    };

    const is30DayCompletion = () => {
        if (compassParticipant && !compassParticipant.end_date) {
            if (getDaysBetweenDates(compassParticipant.enrollment_date, new Date()) > 30) {
                return true;
            }
        }
        return false;
    };

    return (
        <div>
            <Container fluid className="compass-container">
                <Row>
                    <Col md={2}>
                        <h4>{studyID}</h4>
                    </Col>
                    <Col md={4}>
                    </Col>
                    <Col style={{textAlign: 'right'}} md={6}>
                        {
                            compassParticipant && compassParticipant.withdrawn === '1' ? (
                                <Badge bg='danger' style={{marginRight: '8px'}}>Withdrawn</Badge>
                            ) : isReturnVisit() ? (
                                <Badge bg='success' style={{marginRight: '8px'}}>Completed - ED RETURN</Badge>
                            ) : is30DayCompletion() ? (
                                <Badge bg='secondary' style={{marginRight: '8px'}}>Completed - 30 Days</Badge>
                            ) : (
                                <Badge bg='warning' style={{marginRight: '8px'}}>In Progress</Badge>
                            )
                        }
                        {
                            compassParticipant && (
                                <span>
                                    <small>
                                        <strong>MRN: </strong> {compassParticipant.mrn} &bull;{' '} 
                                        <strong>Enrolled Date: </strong> {getLocalDateStrFromDate(new Date(compassParticipant.enrollment_date))} {getLocalTimeStrFromDate(new Date(compassParticipant.enrollment_date))} &bull;{' '} 
                                        <strong>Completion Date: </strong> {compassParticipant.end_date ? <>{getLocalDateStrFromDate(new Date(compassParticipant.end_date))} {getLocalTimeStrFromDate(new Date(compassParticipant.end_date))}</> : "-"}
                                    </small>
                                </span>
                            )
                        }
                    </Col>
                </Row>
                <Row className='mb-2'>
                    <Col md={6}>
                        <ButtonGroup size='sm'>
                            {/* {
                                showVitalsTrends ? (
                                    <Button variant={!showVitalsTrends ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsTrends(false)}}>Hide Vitals Trends</Button>
                                ) : (
                                    <Button variant={showVitalsTrends ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsTrends(true)}}>Show Vitals Trends</Button>
                                )
                            } */}
                            <DataExport 
                                data={patient}
                                variant="outline-secondary"
                            />
                            <Button 
                                variant="outline-secondary"
                                onClick={() => {
                                    // Store data in sessionStorage
                                    sessionStorage.setItem('printReportData', JSON.stringify({
                                        studyId: studyID,
                                        dailyKeyToData,
                                        dateToSurveyAvailability,
                                        compassDates,
                                        serialNumber: serialNumber.current
                                    }));
                                    
                                    // Open the print page
                                    window.open(`/print-report/${studyID}`, '_blank');
                                }}
                            >
                                <FontAwesomeIcon icon={faPrint} /> Print Report
                            </Button>
                        </ButtonGroup>
                        {
                            showVitalsTrends && (
                                <>
                                    <ButtonGroup size='sm' style={{marginLeft: '24px'}}>
                                        {/* <Button variant={showVitalsDay && showVitalsNight ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsDay(true); setShowVitalsNight(true);}}>All</Button> */}
                                        {/* <Button variant={showVitalsDay ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsDay(!showVitalsDay);}}>Daytime</Button>
                                        <Button variant={showVitalsNight ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsNight(!showVitalsNight);}}>Nighttime</Button>
                                        <Button variant={showVitalsWake ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsWake(!showVitalsWake);}}>Wake</Button>
                                        <Button variant={showVitalsSleep ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsSleep(!showVitalsSleep);}}>Sleep</Button> */}
                                        <Button variant={showVitalsDay ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsTimePeriod('day');}}>Daytime</Button>
                                        <Button variant={showVitalsNight ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsTimePeriod('night');}}>Nighttime</Button>
                                        <Button variant={showVitalsWake ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsTimePeriod('wake');}}>Wake</Button>
                                        <Button variant={showVitalsSleep ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsTimePeriod('sleep');}}>Sleep</Button>
                                    </ButtonGroup>
                                    <ButtonGroup size='sm' style={{marginLeft: '24px'}}>
                                        <Button variant={showVitalsActive && showVitalsResting ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsActive(true); setShowVitalsResting(true);}}>All</Button>
                                        <Button variant={showVitalsActive && !showVitalsResting ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsActive(true); setShowVitalsResting(false);}}>Active</Button>
                                        <Button variant={!showVitalsActive && showVitalsResting ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsActive(false); setShowVitalsResting(true);}}>Resting</Button>
                                    </ButtonGroup>
                                    <ButtonGroup size='sm' style={{marginLeft: '24px'}}>
                                        <Button variant={showVitalsMin && showVitalsAvg && showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsMin(true); setShowVitalsAvg(true); setShowVitalsMax(true);}}>All</Button>
                                        <Button variant={showVitalsMin && !showVitalsAvg && !showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsMin(true); setShowVitalsAvg(false); setShowVitalsMax(false);}}>Min</Button>
                                        <Button variant={!showVitalsMin && showVitalsAvg && !showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsMin(false); setShowVitalsAvg(true); setShowVitalsMax(false);}}>Average</Button>
                                        <Button variant={!showVitalsMin && !showVitalsAvg && showVitalsMax ? "secondary" : "outline-secondary"} onClick={() => {setShowVitalsMin(false); setShowVitalsAvg(false); setShowVitalsMax(true);}}>Max</Button>
                                    </ButtonGroup>
                                </>
                            )
                        }
                    </Col>
                    <Col md={6} style={{textAlign: 'right'}}>
                        <ButtonGroup size='sm'>
                            <Button variant={show30Days ? "secondary" : "outline-secondary"} onClick={() => {setShow30Days(true)}}>30 Days</Button>
                            <Button variant={!show30Days ? "secondary" : "outline-secondary"} onClick={() => {setShow30Days(false)}}>All Data</Button>
                        </ButtonGroup>
                    </Col>
                </Row>
                <Row className='compass-table-container'>
                    <Col>
                        <Table striped bordered className='compass-table'>
                            <thead>
                                <tr>
                                    <td><strong>Day</strong></td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => (
                                            <OverlayTrigger
                                                placement="top"
                                                key={idx}
                                                overlay={
                                                    <Tooltip>
                                                        {getPacificDateString(dt)}
                                                    </Tooltip>
                                                }
                                            >
                                                <td key={idx} className={compassActiveDate === getPacificDateString(dt) ? "compass-table-active" : undefined}>
                                                    {idx + 1}
                                                </td>
                                            </OverlayTrigger>
                                        ))
                                    }
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Survey</strong></td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                            let dtStr = getPacificDateString(dt);
                                            let survey = dateToSurveyAvailability[dtStr];
                                            return (
                                                <td key={idx} className={compassActiveDate === dtStr ? "compass-table-active" : undefined} onClick={() => {setCompassActiveDate(dtStr)}}>
                                                    {
                                                        survey ? (
                                                            <span className="compass-ok"><FontAwesomeIcon icon={faCheckCircle} /></span>
                                                        ) : (
                                                            <>-</>
                                                        )
                                                    }
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                                <tr>
                                    <td><FontAwesomeIcon icon={faArrowRight} /> EQ5D</td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                            let dtStr = getPacificDateString(dt);
                                            let survey = dateToSurveyAvailability[dtStr];
                                            return (
                                                <OverlayTrigger
                                                    placement="top"
                                                    key={idx}
                                                    overlay={
                                                        <Popover>
                                                            <Popover.Body>
                                                                <strong>Mobility</strong> {survey ? survey.eq_1 : ""}<br />
                                                                <strong>Self-care</strong> {survey ? survey.eq_2 : ""}<br />
                                                                <strong>Unusual activities</strong> {survey ? survey.eq_3 : ""}<br />
                                                                <strong>Pain/discomfort</strong> {survey ? survey.eq_4 : ""}<br />
                                                                <strong>Anxiety/depression</strong> {survey ? survey.eq_5 : ""}<br />
                                                            </Popover.Body>
                                                        </Popover>
                                                    }
                                                >
                                                    <td key={idx} className={compassActiveDate === dtStr ? "compass-table-active" : undefined} onClick={() => {setCompassActiveDate(dtStr)}}>
                                                        {
                                                            survey ? (
                                                                <>{getEQ5DScore(dateToSurveyAvailability, dtStr)}</>
                                                            ) : (
                                                                <>-</>
                                                            )
                                                        }
                                                    </td>
                                                </OverlayTrigger>
                                            )
                                        })
                                    }
                                </tr>
                                <tr>
                                    <td><FontAwesomeIcon icon={faArrowRight} /> Health</td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                            let dtStr = getPacificDateString(dt);
                                            let survey = dateToSurveyAvailability[dtStr];
                                            return (
                                                <td key={idx} className={compassActiveDate === dtStr ? "compass-table-active" : undefined} onClick={() => {setCompassActiveDate(dtStr)}}>
                                                    {
                                                        survey ? (
                                                            <>{survey.eq_6}</>
                                                        ) : (
                                                            <>-</>
                                                        )
                                                    }
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                                <tr>
                                    <td><strong>Monitor</strong></td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                            let dtStr = getPacificDateString(dt);
                                            let monitor = dateToMonitorAvailability[dtStr];
                                            return (
                                                <td key={idx} className={compassActiveDate === dtStr ? "compass-table-active" : undefined} onClick={() => {setCompassActiveDate(dtStr)}}>
                                                    {
                                                        monitor >= 66.7 ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                key={idx}
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body>
                                                                            Monitor has more than 16 hours of data
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <span className="compass-ok"><FontAwesomeIcon icon={faCheckCircle} /></span>
                                                            </OverlayTrigger>
                                                        ) : monitor > 0 ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                key={idx}
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body>
                                                                            Monitor has less than 16 hours of data
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <span className="compass-warning"><FontAwesomeIcon icon={faCheckCircle} /></span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <>-</>
                                                        )
                                                    }
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                                <tr>
                                    <td><strong>Doctor Visit (Survey)</strong></td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                            let dtStr = getPacificDateString(dt);
                                            let survey = dateToSurveyAvailability[dtStr];
                                            return (
                                                <td key={idx} className={compassActiveDate === dtStr ? "compass-table-active" : undefined} onClick={() => {setCompassActiveDate(dtStr)}}>
                                                    {
                                                        survey && survey.visit_1 === "1" ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                key={idx}
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body>
                                                                            <strong>Visit Description</strong> {survey.visit_2}
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <span className="compass-ok"><FontAwesomeIcon icon={faCheckCircle} /></span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <>-</>
                                                        )
                                                    }
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                                <tr>
                                    <td><strong>Medical Encounters</strong></td>
                                    {
                                        compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                            let dtStr = getPacificDateString(dt);
                                            let isHospital = dateToHospital[dtStr];
                                            let isED = dateToED[dtStr];
                                            let isNonHospital = dateToNonHospital[dtStr];
                                            return (
                                                <td key={idx} className={compassActiveDate === dtStr ? "compass-table-active" : undefined} onClick={() => {setCompassActiveDate(dtStr)}}>
                                                    {
                                                        isED ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                key={idx}
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body>
                                                                            Emergency Department Visit
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <span className="compass-warning"><FontAwesomeIcon icon={faBedPulse} /></span>
                                                            </OverlayTrigger>
                                                        ) : isHospital ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                key={idx}
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body>
                                                                            Hospital Visit
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <span className="compass-ok"><FontAwesomeIcon icon={faHospital} /></span>
                                                            </OverlayTrigger>
                                                        ) : isNonHospital ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                key={idx}
                                                                overlay={
                                                                    <Popover>
                                                                        <Popover.Body>
                                                                            Outpatient Visit
                                                                        </Popover.Body>
                                                                    </Popover>
                                                                }
                                                            >
                                                                <span className="compass-ok"><FontAwesomeIcon icon={faUserDoctor} /></span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <>-</>
                                                        )
                                                    }
                                                </td>
                                            )
                                        })
                                    }
                                </tr>
                                {
                                    showVitalsTrends && vitalTrendKeys.filter((key: string) => {
                                        if (!showVitalsDay && key.includes("_DAYTIME")) {
                                            return false;
                                        }
                                        if (!showVitalsNight && key.includes("_NIGHTTIME")) {
                                            return false;
                                        }
                                        if (!showVitalsWake && key.includes("_WAKE")) {
                                            return false;
                                        }
                                        if (!showVitalsSleep && key.includes("_SLEEP") && key !== "TOTAL_SLEEP_TIME") {
                                            return false;
                                        }
                                        if (!showVitalsActive && key.includes("_ACTIVE")) {
                                            return false;
                                        }
                                        if (!showVitalsResting && key.includes("_RESTING")) {
                                            return false;
                                        }
                                        if (!showVitalsMin && key.includes("_MIN")) {
                                            return false;
                                        }
                                        if (!showVitalsAvg && key.includes("_AVG")) {
                                            return false;
                                        }
                                        if (!showVitalsMax && key.includes("_MAX")) {
                                            return false;
                                        }
                                        return true;
                                    }).map((k) => {
                                        const vals: any[] = Object.values(dailyKeyToData[k]).filter((v: any) => v !== null && v !== undefined);
                                        const min = Math.min(...vals);
                                        const max = Math.max(...vals);
                                        return (
                                            <tr>
                                                <td><strong>{formatVitalTrendLabel(k)}</strong></td>
                                                {
                                                    compassDates.slice(0, show30Days ? 31 : compassDates.length).map((dt: Date, idx: number) => {
                                                        let dtStr = getPacificDateString(dt);
                                                        return (
                                                            <td key={idx} 
                                                                className={compassActiveDate === dtStr ? "compass-table-active" : undefined} 
                                                                onClick={() => {setCompassActiveDate(dtStr)}}
                                                                style={{backgroundColor: getHeatmapColor(dailyKeyToData[k][dtStr], min, max), color: getHeatmapFontColor(dailyKeyToData[k][dtStr], min, max)}}
                                                            >
                                                                {
                                                                    dailyKeyToData[k][dtStr] !== null && dailyKeyToData[k][dtStr] !== undefined ? (
                                                                        <>{Math.round(dailyKeyToData[k][dtStr] * 100) / 100}</>
                                                                    ) : (
                                                                        <>-</>
                                                                    )
                                                                }
                                                            </td>
                                                        )
                                                    })
                                                }
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </Table>
                        {/* <div className='compass-expand' onClick={() => {setShowVitalsTrends(!showVitalsTrends)}}>
                            {
                                showVitalsTrends ? (
                                    <>
                                        <FontAwesomeIcon icon={faChevronUp} /> Hide Vital Trends
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faChevronDown} /> Show Vital Trends
                                    </>
                                )
                            }
                        </div> */}
                    </Col>
                </Row>

                <Tabs
                    activeKey={currentTab}
                    onSelect={(k) => setCurrentTab(k ?? "")}
                    className="mb-3"
                >
                    <Tab eventKey="trends" title="Study Trends">
                        <CompassTrends user={props.user} vitalTrendKeys={vitalTrendKeys} dailyKeyToData={dailyKeyToData} compassDates={compassDates} dateToSurveyAvailability={dateToSurveyAvailability} metricToTrends={metricToTrends} isLoadingMetricToTrends={isLoadingMetricToTrends} />

                        {
                            isLoadingMetricToTrends ? (
                                <><FontAwesomeIcon icon={faSpinner} spin /> Loading Correlations...</>
                            ) : (
                                <>
                                    <h6>Top 10 Correlations for eq5d_avg</h6>
                                    <Table bordered style={{fontSize: '0.9rem'}}>
                                        <thead>
                                            <tr>
                                                <th>feature</th>
                                                <th>rho</th>
                                                <th>p_value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                eq5DCorr && eq5DCorr.map((corr: any, idx: number) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            {corr.feature}
                                                        </td>
                                                        <td>
                                                            {corr.rho}
                                                        </td>
                                                        <td>
                                                            {corr.p_value}
                                                        </td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </Table>

                                    <h6>Top 10 Correlations for eq_6</h6>
                                    <Table bordered style={{fontSize: '0.9rem'}}>
                                        <thead>
                                            <tr>
                                                <th>feature</th>
                                                <th>rho</th>
                                                <th>p_value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                eq6Corr && eq6Corr.map((corr: any, idx: number) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            {corr.feature}
                                                        </td>
                                                        <td>
                                                            {corr.rho}
                                                        </td>
                                                        <td>
                                                            {corr.p_value}
                                                        </td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </Table>
                                </>
                            )
                        }
                    </Tab>
                    <Tab eventKey="daily" title="Daily View">
                        <Card style={{marginBottom: '12px'}}>
                            <Card.Body>
                                <Row>
                                    <Col>
                                        <h5>Encounters</h5>
                                        {
                                            !isLoadingEncounters && filteredEncounters.length > 0 && (
                                                <>
                                                    <Table bordered style={{fontSize: '0.9rem'}}>
                                                        <thead>
                                                            <tr>
                                                                <th>CSN</th>
                                                                <th>Class</th>
                                                                <th>Start</th>
                                                                <th>End</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {
                                                                filteredEncounters.map((encounter: any, idx: number) => (
                                                                    <tr key={idx}>
                                                                        <td>
                                                                            {encounter.resource && encounter.resource.identifier && encounter.resource.identifier.filter((ident: any) => {return ident.use === "usual"}).map((ident: any) => {
                                                                                if (encounter.resource.class && encounter.resource.class.display === "Discharge") {
                                                                                    return (
                                                                                        <>{getEncounterLabel(encounter)}<Button variant='link' size='sm' onClick={() => {redirectToDashboard(ident.value)}}>{ident.value}</Button></>
                                                                                    )
                                                                                } else {
                                                                                    return (
                                                                                        <>{getEncounterLabel(encounter)}{ident.value}</>
                                                                                    )
                                                                                }
                                                                            })}
                                                                        </td>
                                                                        <td>
                                                                            {encounter.resource && encounter.resource.class && encounter.resource.class.display}
                                                                        </td>
                                                                        <td>
                                                                            {encounter.resource && encounter.resource.period.start && (
                                                                                <>{getLocalDateStrFromDate(new Date(encounter.resource.period.start))} {getLocalTimeStrFromDate(new Date(encounter.resource.period.start))}</>
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {encounter.resource && encounter.resource.period.end && (
                                                                                <>{getLocalDateStrFromDate(new Date(encounter.resource.period.end))} {getLocalTimeStrFromDate(new Date(encounter.resource.period.end))}</>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            }
                                                        </tbody>
                                                    </Table>
                                                </>
                                            )
                                        }
                                        {
                                            !isLoadingEncounters && filteredEncounters.length === 0 && (
                                                <Row className="mt-2 mb-2">
                                                    <Col md={12}>
                                                        <strong><FontAwesomeIcon icon={faHospital} /></strong> No medical visits found
                                                    </Col>
                                                </Row>
                                            )
                                        }
                                        {
                                            isLoadingEncounters && (
                                                <Row className="mt-2 mb-2">
                                                    <Col md={12}>
                                                        <Placeholder as="p" animation="glow">
                                                            <Placeholder xs={6} size="xs" />
                                                        </Placeholder>
                                                        <Placeholder as="p" animation="glow">
                                                            <Placeholder xs={6} size="xs" />
                                                        </Placeholder>
                                                        <Placeholder as="p" animation="glow">
                                                            <Placeholder xs={6} size="xs" />
                                                        </Placeholder>
                                                    </Col>
                                                </Row>
                                            )
                                        }
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Card style={{marginBottom: '12px'}}>
                            <Card.Body>
                                {
                                    compassActiveDate && dateToSurveyAvailability[compassActiveDate] ? (
                                        <>
                                            <Row className='mb-2'>
                                                <Col>
                                                    <Row>
                                                        <Col md={4}>
                                                            <h5>Daily Survey</h5>
                                                        </Col>
                                                        <Col md={8} style={{textAlign: 'right'}}>
                                                            <span>
                                                                <strong>Start: </strong> {getLocalDateStrFromDate(new Date(dateToSurveyAvailability[compassActiveDate].start_date))} {getLocalTimeStrFromDate(new Date(dateToSurveyAvailability[compassActiveDate].start_date))} &bull;{' '} 
                                                                <strong>End: </strong> {getLocalDateStrFromDate(new Date(dateToSurveyAvailability[compassActiveDate].end_date))} {getLocalTimeStrFromDate(new Date(dateToSurveyAvailability[compassActiveDate].end_date))}
                                                            </span>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : (
                                        <Row className="mt-2 mb-2">
                                            <Col md={12}>
                                                <strong><FontAwesomeIcon icon={faExclamationCircle} /></strong> No surveys found
                                            </Col>
                                        </Row>
                                    )
                                }
                                {
                                    compassActiveDate && dateToSurveyAvailability[compassActiveDate] && (
                                        <Row>
                                            <Col>
                                                <Table bordered style={{fontSize: '0.9rem'}}>
                                                    <thead>
                                                        <tr>
                                                            <th>Question</th>
                                                            <th>Response</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>Mobility</td>
                                                            <td>{SURVEY_RESPONSE["eq_1"][dateToSurveyAvailability[compassActiveDate].eq_1]} ({dateToSurveyAvailability[compassActiveDate].eq_1})</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Self-care</td>
                                                            <td>{SURVEY_RESPONSE["eq_2"][dateToSurveyAvailability[compassActiveDate].eq_2]} ({dateToSurveyAvailability[compassActiveDate].eq_2})</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Usual activities (e.g., work, study, housework, family or leisure activities):</td>
                                                            <td>{SURVEY_RESPONSE["eq_3"][dateToSurveyAvailability[compassActiveDate].eq_3]} ({dateToSurveyAvailability[compassActiveDate].eq_3})</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Pain/discomfort</td>
                                                            <td>{SURVEY_RESPONSE["eq_4"][dateToSurveyAvailability[compassActiveDate].eq_4]} ({dateToSurveyAvailability[compassActiveDate].eq_4})</td>
                                                        </tr>
                                                        <tr>
                                                            <td>In what areas do you feel pain right now?</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].pain_3)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Anxiety/Depression</td>
                                                            <td>{SURVEY_RESPONSE["eq_5"][dateToSurveyAvailability[compassActiveDate].eq_5]} ({dateToSurveyAvailability[compassActiveDate].eq_5})</td>
                                                        </tr>
                                                        <tr>
                                                            <td>How would you rate your health TODAY from 0 to 100?</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].eq_6)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>How confident are you that you can control and manage most of your health problems? 0 (not at all) to 10 (completely)</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].se_c)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Did you or will you have a doctor or hospital visit today?</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].visit_1)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Please describe the visit</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].visit_2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Did you or will you change any medications or doses today?</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].visit_3)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Please describe any changes</td>
                                                            <td>{formatSurveyResponse(dateToSurveyAvailability[compassActiveDate].visit_4)}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        </Row>
                                    )
                                }
                            </Card.Body>
                        </Card>
                        <VitalSigns 
                            numerics={numerics}
                            rawData={rawData}
                            isLoadingNumerics={isLoadingNumerics}
                            isLoadingRawData={isLoadingWaveform}
                            onTimeSelect={(epochTimeSec: number) => {
                                if (serialNumber.current && compassActiveDate) {
                                    getRawData(
                                        serialNumber.current,
                                        compassActiveDate,
                                        epochTimeSec
                                    );
                                }
                            }}
                            selectedDate={compassActiveDate ? new Date(compassActiveDate) : null}
                        />
                    </Tab>
                    <Tab eventKey="index" title="Index Visit">
                        {
                            indexPatientVisit && (
                                <ul>
                                    <li><strong>Arrival</strong> {getLocalDateStrFromDate(new Date(indexPatientVisit.arrival_time))} {getLocalTimeStrFromDate(new Date(indexPatientVisit.arrival_time))}</li>
                                    <li><strong>Departure</strong> {getLocalDateStrFromDate(new Date(indexPatientVisit.departure_time))} {getLocalTimeStrFromDate(new Date(indexPatientVisit.departure_time))}</li>
                                    <li><strong>Age</strong> {indexPatientVisit.age}</li>
                                    <li><strong>Gender</strong> {indexPatientVisit.gender}</li>
                                    <li><strong>Chief Compliant</strong> {indexPatientVisit.chief_complaint}</li>
                                    <li><strong>Diagnoses</strong> {indexPatientVisit.diagnoses}</li>
                                    <li><strong>Triage HR</strong> {Math.round(indexPatientVisit.triage_hr)}</li>
                                    <li><strong>Triage RR</strong> {Math.round(indexPatientVisit.triage_rr)}</li>
                                    <li><strong>Triage SpO2</strong> {Math.round(indexPatientVisit.triage_spo2)}</li>
                                    <li><strong>Triage Temp</strong> {Math.round(indexPatientVisit.triage_temp * 10) / 10}</li>
                                    <li><strong>Triage BP</strong> {Math.round(indexPatientVisit.triage_sbp)}/{Math.round(indexPatientVisit.triage_dbp)}</li>
                                </ul>
                            )
                        }
                    </Tab>
                </Tabs>
            </Container>  
        </div>
    );
};
